import React, { Fragment } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { useNavigate } from "react-router";

// components
import { RightArrow } from "components/common";

// utils
import { moneyFormatter } from "utils";

const getMethodAmount = (data, code) =>
  data.paymentMethods?.find((m) => m.code === code)?.amount ?? 0;

// 서버가 키오스크를 확정카드에서 따로 떼어 보내므로 합계에 빠지면 현장판매가 줄어든다.
export const ONSITE_PAYMENT_CODES = [
  "CONFIRMED_CARD",
  "KIOSK",
  "MANUAL_CARD",
  "TRANSFER",
  "CASH",
];

export const sumOnsiteAmount = (data) =>
  ONSITE_PAYMENT_CODES.reduce(
    (sum, code) => sum + getMethodAmount(data, code),
    0
  );

const TodaysSales = ({ data, isLoading, isError, onRefresh, ...props }) => {
  const navigate = useNavigate();
  const sections = [
    {
      key: "all",
      label: "총 판매액",
      className: "active",
      valueAmount: data?.totalAmount,
    },
    {
      key: "bpay",
      label: "b.pay",
      valueAmount: data && getMethodAmount(data, "BPAY"),
    },
    {
      key: "crm",
      label: "현장판매",
      valueAmount: data && sumOnsiteAmount(data),
    },
    {
      key: "refund",
      label: "환불",
      // 집계 API는 환불을 양수로 주고, 이 카드는 예전부터 음수로 보였다.
      valueAmount: data && -(data.refund?.amount ?? 0),
    },
  ];

  const renderSections = () => {
    return sections.map((section, index) => {
      return (
        <Fragment key={section.key}>
          <StatsWrapper>
            <div>
              <StatTitle>{section.label}</StatTitle>
            </div>
            <StatAmount className={section.className}>
              {isLoading ? "-" : moneyFormatter(section.valueAmount)}
            </StatAmount>
          </StatsWrapper>

          {index === 0 && <Divider />}
        </Fragment>
      );
    });
  };

  return (
    <MainWrapper onClick={() => navigate("/sales")}>
      <CardTitleWrapper>
        <Title>오늘의 매출</Title>
        <RightArrow size="S" />
      </CardTitleWrapper>

      {isError ? (
        <ErrorWrapper>
          <ErrorText>불러오지 못했어요</ErrorText>
          <RefreshButton
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onRefresh();
            }}
          >
            새로고침
          </RefreshButton>
        </ErrorWrapper>
      ) : (
        renderSections()
      )}
    </MainWrapper>
  );
};

const ErrorWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  gap: 8px;
`;

const ErrorText = styled.p`
  font-size: 16px;
  line-height: 24px;
  color: ${(props) => props.theme.colors.secondaryTextColor};
`;

const RefreshButton = styled.button`
  padding: 0;
  border: none;
  background: none;
  font-size: 16px;
  line-height: 24px;
  color: ${(props) => props.theme.colors.primaryTextColor};
  text-decoration: underline;
`;

const MainWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 12px;
  padding: 24px;
  border-radius: 12px;
  height: 246px;
  background-color: ${(props) => props.theme.colors.cardBackground};
`;

const Divider = styled.div`
  width: 100%;
  border-bottom: 1px solid #f2f2f4;
  margin-top: 6px;
  margin-bottom: 18px;
`;

const CardTitleWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 18px;
`;

const Title = styled.p`
  font-weight: bold;
  font-size: 18px;
  line-height: 24px;
  color: ${(props) => props.theme.colors.primaryTextColor};
`;

const StatsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 12px;
  &.bottom-padding {
  }

  div {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
  }

  span {
    font-size: 14px;
    line-height: 20px;
    margin-left: 4px;
    color: ${(props) => props.theme.colors.secondaryTextColor};
    &.active {
      color: ${(props) => props.theme.colors.activeTextColor};
    }
  }
`;

const StatTitle = styled.p`
  font-size: 16px;
  line-height: 24px;
  letter-spacing: -0.2px;
  color: ${(props) => props.theme.colors.primaryTextColor};
`;

const StatAmount = styled.p`
  font-feature-settings: "tnum" on, "lnum" on;
  font-size: 16px;
  line-height: 24px;
  letter-spacing: -0.2px;
  color: ${(props) => props.theme.colors.primaryTextColor};

  &.active {
    font-size: 18px;
    font-weight: bold;
    color: ${(props) => props.theme.colors.activeTextColor};
  }
`;

TodaysSales.propTypes = {
  data: PropTypes.object,
  isLoading: PropTypes.bool,
  isError: PropTypes.bool,
  onRefresh: PropTypes.func,
};

export default TodaysSales;
